import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Edit3, Mail, Phone, MapPin, UsersRound, UserCog, Trash2, Send, Activity, MessageSquare,
  Users as UsersIcon, Droplet, GitBranch, BookOpen,
} from "lucide-react";

function BookOpenIcon() { return <BookOpen className="h-4 w-4 text-brand" />; }
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { PersonForm } from "./People";
import { DBS_TYPES, FAITH_STATUSES, STAGES, dbsTypeLabel, fmtDate, stageLabel } from "../lib/constants";

interface Person {
  id: string; name: string; email: string | null; phone: string | null;
  source: string | null; pathway_stage: string; faith_status: string;
  group_id: string | null; group_name: string | null; group_dbs_type: string | null;
  assigned_leader_id: string | null; leader_id: string | null; leader_name: string | null;
  dbs_type: string | null; notes: string | null;
  origin?: "self" | "campaign";
  oikos: string | null;
  introduced_by_person_id: string | null; introduced_by_name: string | null;
  baptised_at: number | null;
  baptism_story: string | null;
  created_at: number; updated_at: number;
}
interface Note { id: string; body: string; user_name: string | null; created_at: number }
interface ActivityRow { id: string; action: string; details: string | null; user_name: string | null; created_at: number }
interface Introducee { id: string; name: string; pathway_stage: string; faith_status: string }
interface AttendedSession { id: string; session_date: number; scripture: string | null; group_id: string; group_name: string }

export default function PersonDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [person, setPerson] = useState<Person | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [introduced, setIntroduced] = useState<Introducee[]>([]);
  const [attendedSessions, setAttendedSessions] = useState<AttendedSession[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [leaders, setLeaders] = useState<any[]>([]);
  const [editing, setEditing] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, g, l] = await Promise.all([
      api.get<{
        person: Person; notes: Note[]; activity: ActivityRow[];
        introduced: Introducee[]; attended_sessions: AttendedSession[];
      }>(`/people/${id}`),
      api.get<{ groups: any[] }>("/groups"),
      api.get<{ leaders: any[] }>("/leaders"),
    ]);
    setPerson(d.person); setNotes(d.notes); setActivity(d.activity);
    setIntroduced(d.introduced ?? []);
    setAttendedSessions(d.attended_sessions ?? []);
    setGroups(g.groups); setLeaders(l.leaders);
  };
  useEffect(() => { load(); }, [id]);

  if (!person) return <div className="text-ink-500">Loading…</div>;

  const faith = FAITH_STATUSES.find((f) => f.id === person.faith_status);
  const dbs = DBS_TYPES.find((d) => d.id === person.dbs_type);
  const stage = STAGES.find((s) => s.id === person.pathway_stage);

  const submitNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    setBusy(true);
    try {
      await api.post(`/people/${id}/notes`, { body: noteBody });
      setNoteBody("");
      load();
    } finally { setBusy(false); }
  };

  const deleteNote = async (noteId: string) => {
    if (!confirm("Delete this note?")) return;
    await api.del(`/people/${id}/notes/${noteId}`);
    load();
  };

  const stageQuickChange = async (newStage: string) => {
    await api.patch(`/people/${id}`, { pathway_stage: newStage });
    load();
  };

  // merge notes + activity into single timeline, sorted by time desc
  const timeline = [
    ...notes.map((n) => ({ kind: "note" as const, ts: n.created_at, n })),
    ...activity.map((a) => ({ kind: "activity" as const, ts: a.created_at, a })),
  ].sort((x, y) => y.ts - x.ts);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/people" className="btn-ghost"><ArrowLeft className="h-4 w-4" /> Back to People</Link>
      </div>

      <div className="card p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{person.name}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {faith && <span className="badge bg-ink-50 text-ink-700"><span className={`h-1.5 w-1.5 rounded-full mr-1 ${faith.dot}`} />{faith.label}</span>}
              {stage && <span className={`badge ${stage.color}`}>{stage.label}</span>}
              {dbs && <span className={`badge ${dbs.chip}`}>{dbs.short}</span>}
            </div>
          </div>
          <button className="btn-secondary" onClick={() => setEditing(true)}><Edit3 className="h-4 w-4" /> Edit</button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 text-sm">
          <Field icon={<Phone className="h-4 w-4" />} label="Phone" value={person.phone} />
          <Field icon={<Mail className="h-4 w-4" />} label="Email" value={person.email} />
          <Field icon={<MapPin className="h-4 w-4" />} label="Source" value={person.source} />
          <Field icon={<UsersRound className="h-4 w-4" />} label="Group" value={person.group_name ?? "—"}
            link={person.group_id ? `/groups` : undefined} />
          <Field icon={<UserCog className="h-4 w-4" />} label="Assigned leader" value={person.leader_name ?? "—"} />
          <Field icon={<MessageSquare className="h-4 w-4" />} label="DBS type" value={dbsTypeLabel(person.dbs_type)} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <section className="space-y-4">
          <div className="card p-6">
            <h2 className="font-semibold mb-3">Add a note</h2>
            <form onSubmit={submitNote} className="space-y-2">
              <textarea
                className="input min-h-[80px]"
                placeholder="What happened in your last conversation? Prayer requests, next steps, etc."
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
              />
              <div className="flex justify-end">
                <button className="btn-primary" disabled={busy || !noteBody.trim()}>
                  <Send className="h-4 w-4" /> Post note
                </button>
              </div>
            </form>
          </div>

          <div className="card p-6">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><Activity className="h-4 w-4" /> Notes & activity</h2>
            {timeline.length === 0 && <div className="text-sm text-ink-500">No notes or activity yet.</div>}
            <div className="space-y-3">
              {timeline.map((t) =>
                t.kind === "note" ? (
                  <div key={t.n.id} className="border border-ink-100 rounded-md p-3 bg-ink-50/50">
                    <div className="flex items-center justify-between text-xs text-ink-500">
                      <span><span className="font-medium text-ink-700">{t.n.user_name ?? "—"}</span> · {fmtDate(t.n.created_at)}</span>
                      <button onClick={() => deleteNote(t.n.id)} className="text-ink-400 hover:text-brand"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{t.n.body}</div>
                  </div>
                ) : (
                  <div key={t.a.id} className="flex items-start gap-3 text-sm py-1">
                    <div className="h-2 w-2 rounded-full bg-ink-300 mt-2" />
                    <div className="flex-1">
                      <div className="text-ink-700">
                        <span className="font-medium">{t.a.user_name ?? "Someone"}</span>{" "}
                        {describeActivity(t.a)}
                      </div>
                      <div className="text-xs text-ink-400">{fmtDate(t.a.created_at)}</div>
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="card p-6">
            <h3 className="font-semibold mb-3">Move along pathway</h3>
            <div className="space-y-2">
              {STAGES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => stageQuickChange(s.id)}
                  disabled={s.id === person.pathway_stage}
                  className={`w-full text-left rounded-md p-2 text-sm transition ${
                    s.id === person.pathway_stage
                      ? `${s.color} font-semibold cursor-default`
                      : "hover:bg-ink-100 text-ink-700"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {person.notes && (
            <div className="card p-6">
              <h3 className="font-semibold mb-2">Profile notes</h3>
              <div className="text-sm whitespace-pre-wrap text-ink-700">{person.notes}</div>
            </div>
          )}

          {(person.oikos || person.introduced_by_name || introduced.length > 0) && (
            <div className="card p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><GitBranch className="h-4 w-4 text-brand" /> Relational chain</h3>
              {person.introduced_by_name && (
                <div className="text-sm mb-3">
                  <div className="text-xs text-ink-500 uppercase tracking-wide">Introduced by</div>
                  <Link to={`/people/${person.introduced_by_person_id}`} className="text-ink-900 font-medium hover:text-brand">{person.introduced_by_name}</Link>
                </div>
              )}
              {person.oikos && (
                <div className="text-sm mb-3">
                  <div className="text-xs text-ink-500 uppercase tracking-wide">Oikos (network)</div>
                  <div className="text-ink-700 whitespace-pre-wrap mt-0.5">{person.oikos}</div>
                </div>
              )}
              {introduced.length > 0 && (
                <div className="text-sm">
                  <div className="text-xs text-ink-500 uppercase tracking-wide flex items-center gap-1">
                    <UsersIcon className="h-3.5 w-3.5" /> Introduced ({introduced.length})
                  </div>
                  <ul className="mt-1 space-y-1">
                    {introduced.map((p) => (
                      <li key={p.id}>
                        <Link to={`/people/${p.id}`} className="text-ink-700 hover:text-brand inline-flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-brand" />{p.name}
                          <span className="text-xs text-ink-400">· {stageLabel(p.pathway_stage)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {(person.baptised_at || person.baptism_story) && (
            <div className="card p-6 bg-blue-50 border border-blue-100">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2"><Droplet className="h-4 w-4" /> Baptism</h3>
              {person.baptised_at && (
                <div className="text-sm text-blue-900">Baptised {fmtDate(person.baptised_at)}</div>
              )}
              {person.baptism_story && (
                <div className="text-sm text-blue-900/80 mt-2 whitespace-pre-wrap">{person.baptism_story}</div>
              )}
            </div>
          )}

          {attendedSessions.length > 0 && (
            <div className="card p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <BookOpenIcon /> DBS sessions attended ({attendedSessions.length})
              </h3>
              <ul className="divide-y divide-ink-100">
                {attendedSessions.map((s) => (
                  <li key={s.id} className="py-2 text-sm flex items-center justify-between gap-2">
                    <Link to={`/groups/${s.group_id}`} className="hover:text-brand truncate">
                      <div className="font-medium text-ink-900 truncate">{fmtDate(s.session_date)}</div>
                      <div className="text-xs text-ink-500 truncate">
                        {s.scripture ? `📖 ${s.scripture} · ` : ""}{s.group_name}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <Modal open={editing} onClose={() => setEditing(false)} title="Edit person">
        <PersonForm
          groups={groups}
          leaders={leaders}
          initial={person as any}
          onCreated={() => { setEditing(false); load(); }}
          onCancel={() => setEditing(false)}
          onDelete={async () => {
            if (!confirm("Delete this person?")) return;
            await api.del(`/people/${id}`);
            nav("/people");
          }}
        />
      </Modal>
    </div>
  );
}

function describeActivity(a: ActivityRow): string {
  let details: any = null;
  try { details = a.details ? JSON.parse(a.details) : null; } catch { /* ignore */ }
  switch (a.action) {
    case "created": return "added this person";
    case "stage_changed":
      return `moved from ${stageLabel(details?.from ?? "")} → ${stageLabel(details?.to ?? "")}`;
    case "group_changed": return "changed group assignment";
    case "note_added": return "added a note";
    case "baptised": return "recorded a baptism";
    default: return a.action.replace(/_/g, " ");
  }
}

function Field({ icon, label, value, link }: { icon: React.ReactNode; label: string; value: string | null; link?: string }) {
  const inner = <>
    <div className="text-xs text-ink-500 uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
    <div className="text-ink-900 mt-0.5">{value || "—"}</div>
  </>;
  if (link) return <Link to={link} className="block hover:bg-ink-50 rounded -m-2 p-2">{inner}</Link>;
  return <div>{inner}</div>;
}
